import { apiClient } from './api';
import type { ExerciseStatsRow, FocusedProgressPeriodSummary } from './types';

export interface ExerciseGroupExercise {
  id: number;
  name: string;
  variationName: string | null;
}

export interface ExerciseGroup {
  id: number;
  name: string;
  exercises: ExerciseGroupExercise[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ExerciseGroupListResponse {
  groups: ExerciseGroup[];
}

export interface GroupSeriesPoint {
  date: string | null;
  maxWeight: number;
  estimated1RM: number;
  totalVolume: number;
  avgReps: number;
  avgRPE: number | null;
}

export interface GroupExerciseSeries {
  exerciseId: number;
  exerciseName: string;
  variationName: string | null;
  points: GroupSeriesPoint[];
}

export interface GroupExerciseDeltas {
  exerciseId: number;
  exerciseName: string;
  variationName: string | null;
  summary: {
    '30d': FocusedProgressPeriodSummary;
    '90d': FocusedProgressPeriodSummary;
    '180d': FocusedProgressPeriodSummary;
  };
}

export interface GroupAnalysisResponse {
  groupId: number;
  stats: ExerciseStatsRow[];
  perExerciseSeries: GroupExerciseSeries[];
  deltas: GroupExerciseDeltas[];
}

export interface SaveExerciseGroupInput {
  name?: string;
  exerciseIds?: number[];
}

export class ExerciseGroupService {
  static async listGroups(): Promise<ExerciseGroup[]> {
    const response = await apiClient.get<ExerciseGroupListResponse>('/api/exercise-groups');
    return response.data.groups;
  }

  static async createGroup(input: { name: string; exerciseIds: number[] }): Promise<ExerciseGroup> {
    const response = await apiClient.post<ExerciseGroup>('/api/exercise-groups', input);
    return response.data;
  }

  static async updateGroup(id: number, input: SaveExerciseGroupInput): Promise<ExerciseGroup> {
    const response = await apiClient.put<ExerciseGroup>(`/api/exercise-groups/${id}`, input);
    return response.data;
  }

  static async deleteGroup(id: number): Promise<void> {
    await apiClient.delete(`/api/exercise-groups/${id}`);
  }

  static async getAnalysis(
    id: number,
    params: { dateFrom?: string; dateTo?: string } = {},
  ): Promise<GroupAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    const qs = queryParams.toString();
    const response = await apiClient.get<GroupAnalysisResponse>(
      `/api/exercise-groups/${id}/analysis${qs ? '?' + qs : ''}`,
    );
    return response.data;
  }
}
